// Battle round flow utilities

import { FINAL_ROUND_NUMBER } from '../constants';

/**
 * Determine the result of a battle from a chess.js game instance
 * 
 * @param {Object} game - A chess.js game instance
 * @returns {{ winner: string, reason: string, resultType: string }} Battle result
 */
export function determineBattleResult(game) {
  let winner = 'Draw';
  let reason = 'Game over';
  let resultType = 'draw';

  try {
    if (game.in_checkmate()) {
      // side that just moved delivered mate; winner is opposite of side to move
      const sideToMove = game.turn();
      winner = sideToMove === 'w' ? 'Black' : 'White';
      reason = 'Checkmate';
      resultType = winner === 'White' ? 'win' : 'loss';
    } else if (game.in_stalemate()) {
      winner = 'Draw';
      reason = 'Stalemate';
      resultType = 'draw';
    } else if (game.in_threefold_repetition()) {
      winner = 'Draw';
      reason = 'Threefold repetition';
      resultType = 'draw';
    } else if (game.insufficient_material()) {
      winner = 'Draw';
      reason = 'Insufficient material';
      resultType = 'draw';
    } else if (game.in_draw()) {
      winner = 'Draw';
      reason = 'Draw';
      resultType = 'draw';
    }
  } catch (_) {}

  return { winner, reason, resultType };
}

/**
 * Calculate base gold gain from result type
 * 
 * @param {string} resultType - 'win', 'draw', or 'loss'
 * @returns {number} Base gold gain
 */
export function getBaseGoldGain(resultType) {
  if (resultType === 'win') return 3;
  if (resultType === 'draw') return 1;
  return 0;
}

/**
 * Check if the player has won the campaign
 * 
 * @param {string} winner - 'White', 'Black', or 'Draw'
 * @param {number} currentRound - Current round number
 * @returns {boolean} True if player completed the campaign
 */
export function isCampaignComplete(winner, currentRound) {
  const playerLost = winner === 'Black';
  return !playerLost && winner === 'White' && currentRound >= FINAL_ROUND_NUMBER;
}

/**
 * Get the campaign completion message
 * 
 * @returns {string} Completion message
 */
export function getCampaignCompleteMessage() {
  return `You cleared all ${FINAL_ROUND_NUMBER} rounds!`;
}

/**
 * Update best round in local storage if needed
 * 
 * @param {number} currentRound - Current round number
 * @param {boolean} shouldUpdate - Whether to attempt updating (player lost or won campaign)
 * @returns {number|null} The best round value, or null if not updated
 */
export function updateBestRound(currentRound, shouldUpdate) {
  if (!shouldUpdate) return null;
  try {
    const key = 'gc_best_round';
    const prev = parseInt((window.localStorage.getItem(key) || '0'), 10) || 0;
    const bestRound = Math.max(prev, currentRound);
    if (bestRound !== prev) {
      window.localStorage.setItem(key, String(bestRound));
    }
    return bestRound;
  } catch (_) {
    return null;
  }
}

/**
 * Build the result summary object for display
 * 
 * @param {Object} params - Parameters for building the summary
 * @returns {Object} Result summary object
 */
export function buildResultSummary({
  winner,
  reason,
  resultType,
  currentRound,
  baseGain,
  incomeGain,
  rewardDelta
}) {
  const playerLost = winner === 'Black';
  const campaignComplete = isCampaignComplete(winner, currentRound);
  const bestRound = updateBestRound(currentRound, playerLost || campaignComplete);
  const totalGain = baseGain + incomeGain + rewardDelta;
  const overlayReason = campaignComplete ? getCampaignCompleteMessage() : reason;

  return {
    winner,
    reason: overlayReason,
    normalReason: reason,
    playerLost,
    currentRound,
    bestRound,
    baseGain,
    incomeGain,
    rewardDelta,
    totalGain,
    resultType,
    campaignComplete
  };
}






